import { useCallback, useEffect, useState } from 'react'
import { Icon } from '../components/Icon'
import { Dropdown } from '../components/Dropdown'
import { AdminCategories } from './AdminCategories'
import { getThemeMode, setThemeMode, type ThemeMode } from '../lib/theme'
import './AdminScreen.css'

/**
 * Le panneau `/fitiavana` — décision 2026-08-24.
 *
 * 100 % ONLINE, exception assumée à l'offline-first : rien de ce qui s'affiche
 * ici ne passe par Dexie, et le panneau ne fonctionne pas sans réseau. C'est
 * voulu — il administre le serveur, pas un portefeuille.
 *
 * ⚠️ Tant qu'aucun opérateur SMS n'est branché, ce panneau n'est pas un
 * confort : c'est la SEULE façon d'ouvrir un compte. L'administrateur y lit le
 * code pour le dicter, ou confirme le numéro à la main. Le jour où un vrai
 * opérateur enverra, la lecture des codes devra disparaître d'ici.
 */

const API = import.meta.env?.VITE_API_URL ?? '/api/v1'
const SESSION = 'ariariko:admin_token'

interface Code {
  id: string
  channel: 'sms' | 'email'
  code: string
  destination: string
  delivery: 'pending' | 'sent' | 'failed'
  attempts: number
  usable: boolean
  expires_at: string
  created_at: string
}

interface AdminAccount {
  id: string
  first_name: string | null
  last_name: string | null
  birthdate: string | null
  phone: string
  email: string | null
  phone_verified_at: string | null
  email_verified_at: string | null
  created_at: string
  codes: Code[]
}

type PhoneFilter = 'all' | 'pending' | 'verified'
type EmailFilter = 'all' | 'pending' | 'verified' | 'none'

export function AdminScreen() {
  const [token, setTokenState] = useState<string | null>(
    () => { try { return localStorage.getItem(SESSION) } catch { return null } },
  )
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)

  const [comptes, setComptes] = useState<AdminAccount[]>([])
  const [total, setTotal] = useState(0)
  const [phoneFilter, setPhoneFilter] = useState<PhoneFilter>('all')
  const [emailFilter, setEmailFilter] = useState<EmailFilter>('all')
  const [recherche, setRecherche] = useState('')
  // Deux sujets bien distincts : les gens inscrits, et ce que l'app leur
  // propose au départ. Les mêler sur un seul écran les rendrait tous deux
  // illisibles.
  const [onglet, setOnglet] = useState<'comptes' | 'categories'>('comptes')
  // Écran standalone (routeur court-circuité, voir App.tsx) : pas d'accès à
  // /profil, donc pas d'accès à l'unique contrôle de thème de l'app — il en
  // faut un ici aussi.
  const [theme, setTheme] = useState<ThemeMode>(getThemeMode)

  function chooseTheme(next: ThemeMode) {
    setThemeMode(next)
    setTheme(next)
  }

  function deconnecter() {
    try { localStorage.removeItem(SESSION) } catch { /* privée */ }
    setTokenState(null)
    setComptes([])
  }

  const charger = useCallback(async () => {
    if (!token) return
    const params = new URLSearchParams()
    if (phoneFilter !== 'all') params.set('phone', phoneFilter)
    if (emailFilter !== 'all') params.set('email', emailFilter)
    if (recherche.trim()) params.set('q', recherche.trim())

    try {
      const response = await fetch(`${API}/admin/accounts?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      // Un jeton d'administration expire au bout de huit heures : on ne laisse
      // pas la page afficher une liste vide comme si personne n'était inscrit.
      if (response.status === 401) return deconnecter()
      const payload = await response.json()
      setComptes(payload.accounts ?? [])
      setTotal(payload.total ?? 0)
      setErreur(null)
    } catch {
      setErreur('Serveur injoignable. Ce panneau ne fonctionne pas hors ligne.')
    }
  }, [token, phoneFilter, emailFilter, recherche])

  // `charger` est asynchrone : ses `setState` partent dans une microtâche, pas
  // dans le rendu. C'est bien une synchronisation avec un système externe — le
  // serveur — ce que le linter ne peut pas voir depuis l'appel.
  // oxlint-disable-next-line react/set-state-in-effect
  useEffect(() => { void charger() }, [charger])

  async function connecter(event: React.FormEvent) {
    event.preventDefault()
    setErreur(null)
    setOccupe(true)
    try {
      const response = await fetch(`${API}/admin/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      const payload = await response.json()
      if (!response.ok) { setErreur(payload.error ?? 'Identifiants invalides.'); return }
      try { localStorage.setItem(SESSION, payload.token) } catch { /* privée */ }
      setTokenState(payload.token)
      setPassword('')
    } catch {
      setErreur('Serveur injoignable.')
    } finally {
      setOccupe(false)
    }
  }

  async function agir(id: string, action: 'verify' | 'resend', channel: 'sms' | 'email') {
    try {
      const response = await fetch(`${API}/admin/accounts/${id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ channel }),
      })
      const payload = await response.json()
      if (!response.ok) return setErreur(payload.error ?? 'Action refusée.')
      setErreur(null)
      await charger()
    } catch {
      setErreur('Serveur injoignable.')
    }
  }

  if (!token) {
    return (
      <div className="fitiavana-login">
        <form className="fitiavana-card" onSubmit={(e) => void connecter(e)}>
          <h1>Fitiavana</h1>
          <p>Panneau d'administration Ariariko.</p>
          {erreur && <p className="fitiavana-error" role="alert">{erreur}</p>}
          <label>
            <span>Email</span>
            <input type="email" required autoComplete="username"
                   value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label>
            <span>Mot de passe</span>
            <input type="password" required autoComplete="current-password"
                   value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>
          <button type="submit" disabled={occupe}>
            {occupe ? 'Connexion…' : 'Entrer'}
          </button>
        </form>
      </div>
    )
  }

  return (
    <div className="fitiavana">
      <header className="fitiavana-head">
        <div>
          <h1>Fitiavana</h1>
          <p>{total} compte{total > 1 ? 's' : ''}</p>
        </div>
        <div className="fitiavana-head-actions">
          <div className="fitiavana-theme" role="group" aria-label="Thème">
            <button type="button" aria-pressed={theme === 'system'} aria-label="Suivre l'appareil"
                    onClick={() => chooseTheme('system')}>
              <Icon name="monitor" size={16} />
            </button>
            <button type="button" aria-pressed={theme === 'light'} aria-label="Thème clair"
                    onClick={() => chooseTheme('light')}>
              <Icon name="sun" size={16} />
            </button>
            <button type="button" aria-pressed={theme === 'dark'} aria-label="Thème sombre"
                    onClick={() => chooseTheme('dark')}>
              <Icon name="moon" size={16} />
            </button>
          </div>
          <button type="button" className="fitiavana-out" onClick={deconnecter}>Quitter</button>
        </div>
      </header>

      <div className="fitiavana-tabs" role="group" aria-label="Sections du panneau">
        <button type="button" aria-pressed={onglet === 'comptes'}
                onClick={() => setOnglet('comptes')}>Comptes</button>
        <button type="button" aria-pressed={onglet === 'categories'}
                onClick={() => setOnglet('categories')}>Catégories de départ</button>
      </div>

      {onglet === 'categories' ? (
        <AdminCategories token={token} onExpired={deconnecter} />
      ) : (
      <>
      {erreur && <p className="fitiavana-error" role="alert">{erreur}</p>}

      <div className="fitiavana-filters">
        <input type="search" placeholder="Nom, numéro, email…"
               value={recherche} onChange={(e) => setRecherche(e.target.value)} />
        <label>
          <span>Téléphone</span>
          <Dropdown value={phoneFilter} onChange={setPhoneFilter} ariaLabel="Filtrer par téléphone"
                    options={[
                      { value: 'all', label: 'tous', icon: 'list' },
                      { value: 'pending', label: 'à confirmer', icon: 'history' },
                      { value: 'verified', label: 'confirmé', icon: 'check' },
                    ]} />
        </label>
        <label>
          <span>Email</span>
          <Dropdown value={emailFilter} onChange={setEmailFilter} ariaLabel="Filtrer par email"
                    options={[
                      { value: 'all', label: 'tous', icon: 'list' },
                      { value: 'pending', label: 'à confirmer', icon: 'history' },
                      { value: 'verified', label: 'confirmé', icon: 'check' },
                      { value: 'none', label: 'sans email', icon: 'close' },
                    ]} />
        </label>
      </div>

      {comptes.length === 0 ? (
        <p className="fitiavana-empty">Aucun compte ne correspond.</p>
      ) : (
        <ul className="fitiavana-list">
          {comptes.map((compte) => (
            <li key={compte.id} className="fitiavana-account">
              <div className="fitiavana-identity">
                <strong>{[compte.first_name, compte.last_name].filter(Boolean).join(' ') || '—'}</strong>
                <span className="tabular">{compte.phone}</span>
                {compte.email && <span>{compte.email}</span>}
                {compte.birthdate && <span className="fitiavana-birth">né(e) le {compte.birthdate}</span>}
              </div>

              <div className="fitiavana-states">
                <Etat nom="Téléphone" ouvert={compte.phone_verified_at !== null} />
                <Etat nom="Email"
                      ouvert={compte.email_verified_at !== null}
                      absent={compte.email === null} />
              </div>

              {/* Les codes en clair : c'est tout l'objet de ce panneau tant
                  qu'aucun opérateur n'envoie. */}
              {compte.codes.length > 0 && (
                <ul className="fitiavana-codes">
                  {compte.codes.map((code) => (
                    <li key={code.id} className={code.usable ? '' : 'is-dead'}>
                      <span className="fitiavana-channel">{code.channel}</span>
                      <strong className="tabular">{code.code}</strong>
                      <span className="fitiavana-dest">{code.destination}</span>
                      <span className={`fitiavana-delivery is-${code.delivery}`}>
                        {code.delivery === 'sent' ? 'envoyé'
                          : code.delivery === 'failed' ? 'non envoyé' : 'en attente'}
                      </span>
                      {!code.usable && <span className="fitiavana-dead">périmé</span>}
                      {code.attempts > 0 && <span>{code.attempts} essai(s)</span>}
                    </li>
                  ))}
                </ul>
              )}

              <div className="fitiavana-actions">
                {compte.phone_verified_at === null && (
                  <>
                    <button type="button" className="is-primary"
                            onClick={() => void agir(compte.id, 'verify', 'sms')}>
                      <Icon name="check" size={14} /> Ouvrir le compte
                    </button>
                    <button type="button" onClick={() => void agir(compte.id, 'resend', 'sms')}>
                      Nouveau code SMS
                    </button>
                  </>
                )}
                {compte.email && compte.email_verified_at === null && (
                  <>
                    <button type="button"
                            onClick={() => void agir(compte.id, 'verify', 'email')}>
                      Confirmer l'email
                    </button>
                    <button type="button" onClick={() => void agir(compte.id, 'resend', 'email')}>
                      Nouveau code email
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      </>
      )}
    </div>
  )
}

function Etat({ nom, ouvert, absent }: { nom: string; ouvert: boolean; absent?: boolean }) {
  if (absent) return <span className="fitiavana-state is-none">{nom} : aucun</span>
  return (
    <span className={`fitiavana-state ${ouvert ? 'is-ok' : 'is-wait'}`}>
      {nom} : {ouvert ? 'confirmé' : 'à confirmer'}
    </span>
  )
}
